import {
  SyntheticEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import {
  Add,
  ChakraBox,
  ChakraFlex,
  CircularProgress,
  EmptyList,
  EquipUIFireflyV2Theme,
  InfiniteScroll,
  Reset,
} from '@equip.health/ui';
import { useHistory, useLocation } from 'react-router-dom';

import { NoteEvents } from '~/analytics';
import util from '~/components/common/calendar/util';
import { PAGE_LAYOUT } from '~/lib/constants';
import {
  NoteStatus,
  PatientNoteFilterType,
} from '~/lib/constants/AddPatientNoteModal.constants';
interface PatientNotesTabProps {
  patientId: string;
}
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  getEmptyNotesContent,
  getNotesFilter,
} from '~/lib/util/notes/notes.util';
import { toStateMenuOption } from '~/lib/util/menu';

import PatientNoteFilter from './PatientNoteFilter';
import PatientNoteItem from './PatientNoteItem';
import PatientNotesListItem from './PatientNotesListItem';
import PatientNotesSelectedFilters from './PatientNotesSelectedFilters';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';

import {
  GetNoteListRequest,
  useGetNoteListInfiniteQuery,
} from '~/store/services/notes/notes';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

const GET_NOTE_LIST_PAGE_SIZE = 20;

const TOP_LEVEL_ID = 'patient-notes';

const { primary } = EquipUIFireflyV2Theme.colors;

const isScrollEndReached = (
  lastChild: HTMLElement,
  noteDetailListBottom: number,
): boolean => {
  if (lastChild?.getBoundingClientRect()?.bottom) {
    const lastChildBottom = lastChild.getBoundingClientRect().bottom;
    if (lastChildBottom - noteDetailListBottom < 50) {
      return true;
    }
  }
  return false;
};

const parseNoteIdFromScrollableElementId = (id: string) =>
  id.replace(`${TOP_LEVEL_ID}__item-detail-view__`, '');

const expandedNoteListOnScroll = (
  event: SyntheticEvent,
  setSelectedNoteId: (string) => void,
) => {
  const noteDetailList = event.target as HTMLDivElement;
  if (!noteDetailList) return;

  const {
    bottom: noteDetailListBottom,
    height: noteDetailListHeight,
    top: noteDetailListTop,
  } = noteDetailList.getBoundingClientRect();

  const childNodes = noteDetailList.childNodes as NodeListOf<HTMLElement>;
  if (!childNodes || ![childNodes].length) return;

  const childNodesArray = Array.from(childNodes);
  const childNodesLength = childNodesArray.length;

  let currentSelectedNoteId: string | null = null;

  if (
    isScrollEndReached(
      childNodesArray[childNodesLength - 1],
      noteDetailListBottom,
    )
  ) {
    currentSelectedNoteId = parseNoteIdFromScrollableElementId(
      childNodesArray[childNodesLength - 1].id,
    );
  } else {
    childNodesArray.every((element) => {
      const { bottom: currentNodeBottom, top: currentNodeTop } =
        element.getBoundingClientRect();

      const currentScrollPosition = Math.abs(
        currentNodeTop - noteDetailListTop,
      );

      // Check current element top is near to top of the list parent
      if (currentScrollPosition <= 200) {
        currentSelectedNoteId = parseNoteIdFromScrollableElementId(element.id);
        return false;
      }

      /* Check current element top lies in the visible part
      and its height is greater than half of visible area height */
      if (
        currentNodeTop < noteDetailListTop &&
        currentNodeBottom > noteDetailListTop &&
        currentNodeBottom - currentNodeTop >
          noteDetailListHeight / 2 + noteDetailListTop
      ) {
        currentSelectedNoteId = parseNoteIdFromScrollableElementId(element.id);
        return false;
      }
      return true;
    });
  }
  if (currentSelectedNoteId !== null) {
    setSelectedNoteId(currentSelectedNoteId);
  }
};

const Loading = ({ height = 50 }: { height?: number }) => (
  <ChakraFlex
    alignItems="center"
    height={`${height}px`}
    justifyContent="center"
    width="100%"
  >
    <CircularProgress
      id={`${TOP_LEVEL_ID}__patient-notes-loading`}
      isIndeterminate
      padding="8px"
      size="30px"
    />
  </ChakraFlex>
);

interface AppointmentData {
  appointment: AppointmentDetailResponse;
}
const patientNoteListScrollableTarget = 'PatientNoteListScrollableTarget';

const PatientNotesTab = ({ patientId }: PatientNotesTabProps) => {
  const { trackEvent } = useAnalytics();

  const { currentUser } = useGlobalContext();
  const { openNoteDrawer } = useEditNoteDrawerContext();
  const history = useHistory();
  const location = useLocation();
  const historyState = location?.state as AppointmentData;

  // Extract query params for auto-opening notes from new tab navigation
  const queryParams = new URLSearchParams(location.search);
  const noteIdFromQuery = queryParams.get('noteId');
  const appointmentIdFromQuery = queryParams.get('appointmentId');
  const appointmentTitleFromQuery = queryParams.get('appointmentTitle');

  const documentNavigationState = location?.state as {
    documentId?: string;
    documentName?: string;
    fromDocumentsTableRow?: boolean;
  };

  const [selectedNoteId, setSelectedNoteId] = useState<string>('');
  const [
    disableAutoScrollOnUserSelection,
    setDisableAutoScrollOnUserSelection,
  ] = useState<boolean>(false);

  const [filterValue, setFilterValue] = useState<PatientNoteFilterType>(() =>
    getNotesFilter(documentNavigationState),
  );

  const appointmentExternalId = filterValue?.sessionLink?.value;

  const getNoteListFilterBy = useMemo<GetNoteListRequest['filterBy']>(() => {
    const filterBy: GetNoteListRequest['filterBy'] = {};
    if (filterValue?.creator?.length) {
      filterBy.createdByProviderExternalId = filterValue.creator.map(
        (item) => item?.value,
      );
    }

    if (filterValue?.status?.length) {
      filterBy.noteStatus = filterValue.status.map((item) => item?.value);
    }

    if (filterValue?.noteType?.length) {
      filterBy.noteType = filterValue.noteType.map((item) => item?.value);
    }

    if (filterValue?.creatorRole?.length) {
      filterBy.providerRole = filterValue.creatorRole.map(
        (item) => item?.value,
      );
    }

    if (filterValue?.title) {
      filterBy.title = filterValue.title;
    }
    if (filterValue?.showMyNotes) {
      filterBy.createdByProviderExternalId = [
        ...(filterBy.createdByProviderExternalId ?? []),
        currentUser.userId,
      ];
    }
    if (filterValue?.showNotesWithAttachment) {
      filterBy.hasAttachments = true;
    }

    const appointmentExtId =
      filterValue?.sessionLink?.value ||
      appointmentExternalId ||
      historyState?.appointment?.appointmentExternalId;
    if (appointmentExtId) {
      filterBy.appointmentExternalId = appointmentExtId;
    }
    if (filterValue?.documentName?.value) {
      filterBy.documentId = filterValue.documentName.value;
    }

    return filterBy;
  }, [filterValue]);

  const {
    data: noteListResponse,
    isLoading: isGetNoteListLoading,
    fetchNextPage,
  } = useGetNoteListInfiniteQuery(
    {
      patientId,
      pageSize: GET_NOTE_LIST_PAGE_SIZE,
      filterBy: getNoteListFilterBy,
    },
    { skip: !patientId || !getNoteListFilterBy },
  );

  const noteList = useMemo(
    () => noteListResponse?.pages.flatMap((page) => page.data) ?? [],
    [noteListResponse],
  );
  const totalCount = useMemo(
    () => noteListResponse?.pages[0]?.count,
    [noteListResponse],
  );

  useEffect(() => {
    if (selectedNoteId === '') {
      const patientNoteListElement = document.getElementById(
        patientNoteListScrollableTarget,
      );
      if (patientNoteListElement) {
        patientNoteListElement.scrollTop = 0;
        return;
      }
    }

    const selectedItemExpandedHTMLElement = document.getElementById(
      `${TOP_LEVEL_ID}__list-item__${selectedNoteId}`,
    );
    if (!selectedItemExpandedHTMLElement) return;
    selectedItemExpandedHTMLElement.scrollIntoView({
      behavior: 'auto',
      block: 'nearest',
    });
  }, [selectedNoteId]);

  // Check if this is coming from appointments tab
  useLayoutEffect(() => {
    if (historyState?.appointment) {
      const appointmentData = historyState?.appointment;
      if (appointmentData) {
        setFilterValue((prevData) => ({
          ...prevData,
          sessionLink: util.convertAppointmentDataToMenuItem(appointmentData),
        }));
      }
    }
  }, [historyState]);

  /**
   * When user manually select a note item in left panel, Selected item will be scrolled in note list expanded view
   * and disableAutoScrollOnUserSelection is set to true to disable onScroll call in note list expanded view.
   *
   * whenever disableAutoScrollOnUserSelection is set true, it will be reset in 50ms
   */
  useEffect(() => {
    let resetAutoScrollTimer;
    if (disableAutoScrollOnUserSelection) {
      resetAutoScrollTimer = setTimeout(
        () => setDisableAutoScrollOnUserSelection(false),
        500,
      );
    }
    return () => {
      if (resetAutoScrollTimer) clearTimeout(resetAutoScrollTimer);
    };
  }, [disableAutoScrollOnUserSelection]);

  const onClickNoteListItem = (noteId: string) => {
    setDisableAutoScrollOnUserSelection(true);
    setSelectedNoteId(noteId);
    document
      .getElementById(`${TOP_LEVEL_ID}__item-detail-view__${noteId}`)
      ?.scrollIntoView({
        behavior: 'auto',
        block: 'start',
      });
  };
  const handleRemoveFilter = ({ keyName, value }) => {
    if (keyName === 'documentName') {
      setFilterValue((prev) => ({
        ...prev,
        documentName: null,
      }));
      return;
    }

    if (Array.isArray(filterValue[keyName])) {
      const tempFilter = filterValue[keyName].filter(
        ({ value: filterItemValue }) => filterItemValue !== value,
      );
      setFilterValue({ ...filterValue, [keyName]: tempFilter });
    } else {
      setFilterValue({ ...filterValue, [keyName]: null });
    }
  };

  const isFilterApplied = useMemo(
    () => Object.keys(getNoteListFilterBy).length > 0,
    [getNoteListFilterBy],
  );
  useEffect(() => {
    // If this is coming from "Appointments" page
    if (historyState?.appointment) {
      if (appointmentExternalId) {
        historyState.appointment = null;
      }
    }
  }, [filterValue]);

  // Auto-open note from query params (used when opening in new tab)
  useEffect(() => {
    if (noteList?.length > 0 && noteIdFromQuery) {
      // Set appointment filter if provided
      if (appointmentIdFromQuery) {
        setFilterValue((prevData) => ({
          ...prevData,
          sessionLink: toStateMenuOption({
            externalId: appointmentIdFromQuery,
            label: appointmentTitleFromQuery || 'Session',
          }),
        }));
      }

      onClickNoteListItem(noteIdFromQuery);
      const matchedNote = noteList.find(
        (n) => n.noteExternalId === noteIdFromQuery,
      );
      if (matchedNote?.noteStatus !== NoteStatus.SIGNED) {
        openNoteDrawer(patientId, noteIdFromQuery);
      }

      // Clear query params after opening to prevent reopening
      history.replace({
        pathname: location.pathname,
        state: location.state,
      });
    }
  }, [
    noteIdFromQuery,
    appointmentIdFromQuery,
    appointmentTitleFromQuery,
    noteList,
    openNoteDrawer,
    patientId,
    history,
    location.pathname,
    location.state,
  ]);

  return (
    <ChakraFlex maxHeight="100%">
      <ChakraFlex
        direction="column"
        overflow="hidden"
        paddingX="16px"
        width="382px"
      >
        <ChakraBox>
          <PatientNoteFilter
            filterValue={filterValue}
            patientId={patientId}
            setFilterValue={(value: PatientNoteFilterType) => {
              setFilterValue(value);
            }}
          />
          {filterValue && (
            <PatientNotesSelectedFilters
              filters={filterValue}
              handleRemove={handleRemoveFilter}
              handleReset={() => {
                setFilterValue(null);
              }}
            />
          )}
        </ChakraBox>
        <ChakraBox
          id={patientNoteListScrollableTarget}
          overflow="auto"
          position="relative"
        >
          {noteList.length > 0 ? (
            <InfiniteScroll
              dataLength={noteList.length}
              fetchData={() => {
                fetchNextPage();
              }}
              hideCount
              id={TOP_LEVEL_ID}
              scrollableTarget={patientNoteListScrollableTarget}
              showLoader={false}
              totalCount={totalCount}
            >
              {noteList.map((note, index) => (
                <ChakraBox
                  id={`${TOP_LEVEL_ID}__list-item__${index}`}
                  key={`Notes::${note?.noteExternalId}}`}
                >
                  <PatientNotesListItem
                    index={index}
                    isSelected={selectedNoteId === note.noteExternalId}
                    key={`Notes::${note?.noteExternalId}}`}
                    note={note}
                    onClickNoteListItem={() => {
                      trackEvent(NoteEvents.notesOnLHRailNotesTabClicked, {
                        Source: AnalyticsSourceValues.NOTES_TAB,
                      });
                      onClickNoteListItem(note.noteExternalId);
                    }}
                    prevNote={index > 0 ? noteList[index - 1] : null}
                  />
                </ChakraBox>
              ))}

              {isGetNoteListLoading && <Loading />}
            </InfiniteScroll>
          ) : (
            <>
              {isGetNoteListLoading ? (
                <Loading height={250} />
              ) : (
                <EmptyList
                  actionButtons={
                    isFilterApplied
                      ? [
                          {
                            buttonText: 'Reset Filter',
                            key: 'resetFilter',
                            onClick: () => {
                              setFilterValue(null);
                            },
                            rightIcon: (
                              <Reset
                                fill={primary[100]}
                                height="16px"
                                width="16px"
                              />
                            ),
                          },
                        ]
                      : [
                          {
                            buttonText: 'Add note',
                            key: 'addNotes',
                            onClick: () => {
                              openNoteDrawer(patientId, null);
                            },
                            rightIcon: (
                              <Add
                                fill={primary[100]}
                                height="16px"
                                width="16px"
                              />
                            ),
                          },
                        ]
                  }
                  description={getEmptyNotesContent(
                    'description',
                    isFilterApplied,
                  )}
                  hidePrimaryText={!isFilterApplied}
                  id={TOP_LEVEL_ID}
                  marginTop="20px"
                  primaryText={getEmptyNotesContent(
                    'primaryText',
                    isFilterApplied,
                  )}
                  secondaryText={getEmptyNotesContent(
                    'secondaryText',
                    isFilterApplied,
                  )}
                />
              )}
            </>
          )}
        </ChakraBox>
      </ChakraFlex>

      <ChakraBox
        alignItems="stretch"
        flex="1"
        id={`${TOP_LEVEL_ID}__expanded-list`}
        onScroll={(event: SyntheticEvent) => {
          if (disableAutoScrollOnUserSelection) return;
          expandedNoteListOnScroll(event, setSelectedNoteId);
        }}
        overflow="auto"
        paddingRight={PAGE_LAYOUT.rightSpacing}
        position="relative"
      >
        {Boolean(noteList.length) && (
          <>
            {noteList.map((note) => (
              <ChakraBox
                id={`${TOP_LEVEL_ID}__item-detail-view__${note.noteExternalId}`}
                key={`Notes::${note?.noteExternalId}}`}
              >
                <PatientNoteItem note={note} />
              </ChakraBox>
            ))}
          </>
        )}
      </ChakraBox>
    </ChakraFlex>
  );
};

export default PatientNotesTab;
